<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import Tabs from "./Tabs.vue";
import Questions from "./questions/Questions.vue";
import Styles from "./style/Styles.vue";
import Context from "./context/Context.vue";
import GeneratedResult from "./result/GeneratedResult.vue";

const store = useStore();
defineEmits(["showGenerateButtons"]);

const isMobile = computed(() => store.state.isMobile);
const isOpen = computed(() => store.state.drawer.isOpen);
const tabList = computed(() => store.state.tabs.tabList);
const currentTab = computed(() => store.state.tabs.currentTab);
const showResult = computed(() => store.state.result.showResult);
const wide = computed(() => showResult.value && store.state.result.expanded);

const setDefaultTab = () => store.dispatch("tabs/setDefaultTab");
const setTab = (tab) => store.dispatch("tabs/setTab", tab);

const genRes = ref(null);

setDefaultTab();
const currentTabComponent = computed(() => {
  switch (currentTab.value.componentName) {
    case "Context":
      return Context;
    case "Questions":
      return Questions;
    case "Styles":
      return Styles;
    default:
      return Questions;
  }
});
</script>

<template>
  <!--Tabs Desktop: two thirds tabs and one third make, or the other way round
  when the make is expanded to read-->
  <div v-if="isOpen && !isMobile" class="mt-5 hidden lg:flex">
    <div
      class="tabs-column tabs flex min-w-0 shrink-0 flex-col overflow-x-hidden"
      :class="{ 'is-narrow': wide }"
    >
      <Tabs
        ref="tabs"
        :tab-list="tabList"
        :current-tab="currentTab"
        @change="setTab"
      />
      <div>
        <transition name="fade" mode="out-in" appear :duration="500">
          <component class="mt-6" :is="currentTabComponent" />
        </transition>
      </div>
    </div>
    <div v-if="showResult" class="min-w-0 flex-1">
      <GeneratedResult ref="genRes" />
    </div>

    <div v-else class="flex min-w-0 flex-1 justify-center">
      <img class="h-[266px] animate-fade_in_genie" src="/genie-crossed.svg" />
    </div>
  </div>

  <!--Tabs Mobile-->
  <div v-if="isOpen && isMobile" class="block lg:hidden">
    <div v-if="showResult">
      <GeneratedResult
        ref="genRes"
        @showGenerateButtons="$emit('showGenerateButtons', $event)"
      />
    </div>
    <!-- Grows with its tab, so the white sheet always runs past the last question;
      the padding keeps the end clear of the bar pinned to the bottom -->
    <div v-else class="relative tabs">
      <component
        class="pb-[calc(8rem+env(safe-area-inset-bottom))]"
        :is="currentTabComponent"
      />
    </div>
  </div>
</template>

<style scoped>
/* Expanding the make swaps the widths: the tabs slide to a third and the make takes
   two thirds, so the questions stay there to change */
.tabs-column {
  flex-basis: calc(66.666% - 2.75rem);
  margin-right: 2.75rem;
  transition: flex-basis 450ms cubic-bezier(0.2, 0.8, 0.2, 1);
}
.tabs-column.is-narrow {
  flex-basis: calc(33.333% - 2.75rem);
}
@media (prefers-reduced-motion: reduce) {
  .tabs-column {
    transition: none;
  }
}
</style>
